// Billing DTOs + inputs. docs/replan-v2.md R6′.
//
// Amounts are always minor units (fils/cents) server-side; `displayAmount` is
// the pre-formatted major-unit string the UI renders. Currency + interval are
// closed enums matching the DB.

import { z } from 'zod';

export const currencySchema = z.enum(['QAR', 'AED', 'SAR', 'KWD', 'BHD', 'OMR', 'USD']);
export type Currency = z.infer<typeof currencySchema>;

export const intervalSchema = z.enum(['month', 'year']);
export type Interval = z.infer<typeof intervalSchema>;

export const subscriptionStatusSchema = z.enum([
  'trialing',
  'active',
  'past_due',
  'canceled',
  'incomplete',
]);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

// 🔴 `refunded` and `partially_refunded` were added to the DB enum by migration
// 0011 but never to this schema. Because these schemas validate route OUTPUT, a
// refunded invoice would have failed validation on its way out of the API.
//
// `void` keeps its own distinct meaning — an invoice cancelled BEFORE it was
// ever paid. A refund never produces `void`; see lib/refunds.ts.
export const invoiceStatusSchema = z.enum([
  'draft',
  'open',
  'paid',
  'void',
  'uncollectible',
  'refunded',
  'partially_refunded',
]);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const refundStatusSchema = z.enum(['pending', 'succeeded', 'failed']);
export type RefundStatus = z.infer<typeof refundStatusSchema>;

// ─── Catalog DTOs ─────────────────────────────────────────────────────────────

export const planPriceDto = z.object({
  currency: currencySchema,
  interval: intervalSchema,
  amountMinor: z.number().int(),
  displayAmount: z.string(),
});
export type PlanPriceDto = z.infer<typeof planPriceDto>;

export const planDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  audience: z.enum(['family', 'school']),
  tier: z.string(),
  nameKey: z.string(),
  taglineKey: z.string().nullable(),
  features: z.array(z.string()),
  isHighlighted: z.boolean(),
  maxKids: z.number().int().nullable(),
  maxTeachers: z.number().int().nullable(),
  maxStudents: z.number().int().nullable(),
  prices: z.array(planPriceDto),
});
export type PlanDto = z.infer<typeof planDto>;

// ─── Parent/school billing ────────────────────────────────────────────────────

export const subscriptionDto = z.object({
  id: z.uuid(),
  status: subscriptionStatusSchema,
  planId: z.uuid(),
  planNameKey: z.string(),
  currency: currencySchema,
  interval: intervalSchema,
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
});
export type SubscriptionDto = z.infer<typeof subscriptionDto>;

export const invoiceDto = z.object({
  id: z.uuid(),
  amountMinor: z.number().int(),
  currency: currencySchema,
  status: invoiceStatusSchema,
  issuedAt: z.string(),
  dueAt: z.string().nullable(),
  paidAt: z.string().nullable(),
});
export type InvoiceDto = z.infer<typeof invoiceDto>;

// Start (or change to) a plan. Creates an open invoice + a checkout session.
export const checkoutInput = z.object({
  planId: z.uuid(),
  currency: currencySchema,
  interval: intervalSchema,
  couponCode: z.string().trim().min(1).max(60).optional(),
});
export type CheckoutInput = z.infer<typeof checkoutInput>;

export const checkoutResultDto = z.object({
  checkoutUrl: z.string(),
  invoiceId: z.uuid(),
  subscriptionId: z.uuid(),
});
export type CheckoutResultDto = z.infer<typeof checkoutResultDto>;

// ─── Admin: plan management ───────────────────────────────────────────────────

export const adminPlanUpsertInput = z.object({
  slug: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9-]{1,58}$/, 'lowercase slug'),
  audience: z.enum(['family', 'school']),
  tier: z.string().trim().min(1).max(40),
  nameKey: z.string().trim().min(1).max(240),
  taglineKey: z.string().trim().max(240).nullable().optional(),
  features: z.array(z.string()).max(30).optional().default([]),
  isHighlighted: z.boolean().optional().default(false),
  // 🔴 null = UNLIMITED, and that is NOT the same as 0. `.positive()` is what
  // keeps 0 out: a plan capped at zero kids would be unusable but look valid,
  // and the console has no way to render the difference between "0 kids" and
  // "no limit" if both are storable. Unlimited is expressed as an explicit
  // null, never as a missing key on a PATCH (which means "leave unchanged").
  maxKids: z.number().int().positive().nullable().optional(),
  maxTeachers: z.number().int().positive().nullable().optional(),
  maxStudents: z.number().int().positive().nullable().optional(),

  // Trial config is PER PLAN (owner ruling 2026-08-16). The columns have
  // existed on `plans` since M7 but were never surfaced through this schema,
  // so the API silently dropped them on create and update — the DB defaults
  // (0 / 0) were the only values any plan could ever hold.
  //
  // 0 = no trial, which is the default, so untouched plans keep behaving
  // exactly as before. Capped at 365: a "trial" longer than a year is a free
  // plan wearing a costume, and an unbounded value here gives away the
  // product. trialRequiresCard is only meaningful when trialDays > 0; that
  // cross-field rule is `trialPairIsCoherent` below (a predicate, not a
  // .refine() — see the comment there for why).
  trialDays: z.number().int().min(0).max(365).optional(),
  trialRequiresCard: z.boolean().optional(),

  status: z.enum(['active', 'archived']).optional().default('active'),
});
export type AdminPlanUpsertInput = z.infer<typeof adminPlanUpsertInput>;

/**
 * PATCH body. Every key optional — an absent key means "leave unchanged",
 * which is why the limits are `.nullable()` rather than merely optional: null
 * is the only way to say "make this unlimited".
 */
export const adminPlanPatchInput = adminPlanUpsertInput.partial();
export type AdminPlanPatchInput = z.infer<typeof adminPlanPatchInput>;

/**
 * The trial pair's cross-field rule, as a plain predicate rather than a
 * `.refine()` on the schemas above.
 *
 * 🔴 WHY NOT A REFINEMENT. `@hono/zod-validator` types `c.req.valid('json')`
 * from the schema's INPUT type. `adminPlanUpsertInput` has `.default()` on
 * `features`, `isHighlighted` and `status`, so those are optional on input and
 * required on output — the bare ZodObject gets unwrapped and the handler sees
 * the output type, but wrapping it in `.refine()` makes the handler see the
 * INPUT type instead, and `input.status` stops existing. Rather than litter
 * the route with `?? 'active'` fallbacks that would quietly re-implement the
 * defaults, the rule lives here and the route calls it.
 *
 * The rule itself: `trialRequiresCard: true` with `trialDays: 0` is not a
 * harmless no-op — it is a card requirement attached to a trial that does not
 * exist, and the next operator to set trialDays would silently inherit it.
 *
 * A PATCH carrying only one of the two keys cannot be judged from the body
 * alone, so the route merges each field with the stored row before calling
 * this. See `apps/api/src/routes/admin/plans.ts`.
 */
export function trialPairIsCoherent(trialDays: number, trialRequiresCard: boolean): boolean {
  return !(trialRequiresCard && trialDays === 0);
}

export const TRIAL_PAIR_MESSAGE = 'trialRequiresCard needs a trial: set trialDays above 0';

/**
 * Zod OUT for `GET /admin/plans` (CLAUDE.md §5: Zod in, Zod out).
 *
 * `plans.features` is an untyped `jsonb` column, so the row Drizzle hands back
 * is `unknown` — the admin console was casting it to `string[]` on trust. A
 * legacy row holding an object, a nested array, or a number would have reached
 * React and thrown at render. Parsing here turns that into a server-side 500
 * with a real message instead of a blank console screen.
 */
export const adminPlanDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  audience: z.enum(['family', 'school']),
  tier: z.string(),
  nameKey: z.string(),
  taglineKey: z.string().nullable(),
  features: z.array(z.string()),
  isHighlighted: z.boolean(),
  maxKids: z.number().int().nullable(),
  maxTeachers: z.number().int().nullable(),
  maxStudents: z.number().int().nullable(),
  trialDays: z.number().int(),
  trialRequiresCard: z.boolean(),
  status: z.string(),
  prices: z.array(
    z.object({
      currency: currencySchema,
      interval: intervalSchema,
      amountMinor: z.number().int(),
      displayAmount: z.string(),
    }),
  ),
});
export type AdminPlanDto = z.infer<typeof adminPlanDto>;

export const adminPriceUpsertInput = z.object({
  currency: currencySchema,
  interval: intervalSchema,
  amountMinor: z.number().int().nonnegative(),
  displayAmount: z.string().trim().min(1).max(20),
});
export type AdminPriceUpsertInput = z.infer<typeof adminPriceUpsertInput>;

// ─── Admin: payments view ─────────────────────────────────────────────────────

export const adminInvoiceRowDto = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  ownerType: z.enum(['family', 'school']),
  ownerLabel: z.string(),
  amountMinor: z.number().int(),
  currency: currencySchema,
  status: invoiceStatusSchema,
  issuedAt: z.string(),
  paidAt: z.string().nullable(),
});
export type AdminInvoiceRowDto = z.infer<typeof adminInvoiceRowDto>;

// ─── M7c: refunds, payment methods, invoice detail ───────────────────────────

export const adminRefundInput = z.object({
  // Minor units. Omit to refund the full remaining refundable balance —
  // NOT the invoice total, which would over-refund a partially-refunded row.
  amountMinor: z.number().int().positive().optional(),
  // Required and non-blank: `refunds.reason` is NOT NULL and is the only
  // record of WHY money moved.
  reason: z.string().trim().min(1).max(500),
});
export type AdminRefundInput = z.infer<typeof adminRefundInput>;

export const refundDto = z.object({
  id: z.uuid(),
  invoiceId: z.uuid(),
  amountMinor: z.number().int(),
  currency: currencySchema,
  reason: z.string(),
  status: refundStatusSchema,
  providerRef: z.string().nullable(),
  createdAt: z.string(),
  refundedAt: z.string().nullable(),
});
export type RefundDto = z.infer<typeof refundDto>;

// A stored card as the CUSTOMER may see it.
// 🔴 There is deliberately no `token` field — that is the provider's tokenId
// and must never leave the server (CLAUDE.md §4).
export const paymentMethodDto = z.object({
  id: z.uuid(),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  expMonth: z.number().int().nullable(),
  expYear: z.number().int().nullable(),
  isDefault: z.boolean(),
  isExpired: z.boolean(),
  isExpiringSoon: z.boolean(),
  createdAt: z.string(),
});
export type PaymentMethodDto = z.infer<typeof paymentMethodDto>;

// One invoice with its refund ledger. `planId` legitimately differs from the
// subscription's plan while a plan change awaits payment.
export const invoiceDetailDto = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  amountMinor: z.number().int(),
  currency: currencySchema,
  status: invoiceStatusSchema,
  subtotalMinor: z.number().int().nullable(),
  discountMinor: z.number().int(),
  couponCode: z.string().nullable(),
  planId: z.uuid().nullable(),
  planSlug: z.string().nullable(),
  issuedAt: z.string(),
  dueAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  providerRef: z.string().nullable(),
  refunds: z.array(refundDto),
  refundedMinor: z.number().int(),
});
export type InvoiceDetailDto = z.infer<typeof invoiceDetailDto>;

// 🔴 `refundableMinor` is an ADMIN concept — it answers "how much may staff
// still refund?" — and it belongs nowhere near a customer screen: a refundable
// balance shown to the person who paid reads as an offer.
//
// It used to sit on `invoiceDetailDto` above, declared but never emitted by
// GET /billing/invoices/:id. Because that response is `c.json(...)` and not
// zValidator-checked on output, the divergence was silent and anything typed
// against the DTO read `undefined` at runtime. Only the admin handler
// (routes/admin/payments.ts) ever emitted it — hence this split. M7e §4.3.
export const adminInvoiceDetailDto = invoiceDetailDto.extend({
  refundableMinor: z.number().int(),
});
export type AdminInvoiceDetailDto = z.infer<typeof adminInvoiceDetailDto>;

// A merged money timeline. Invoices are positive entries, succeeded refunds
// negative. Grouped by currency at the presentation layer — a single summed
// total across seven currencies would be meaningless.
export const transactionDto = z.object({
  id: z.uuid(),
  kind: z.enum(['invoice', 'refund']),
  amountMinor: z.number().int(),
  currency: currencySchema,
  status: z.string(),
  description: z.string(),
  occurredAt: z.string(),
  invoiceId: z.uuid(),
});
export type TransactionDto = z.infer<typeof transactionDto>;
